import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RotateCcw, RefreshCw, Trash2 } from 'lucide-react';
import { getApiUrl } from '../../utils/api';
import { EmptyState, TableRowsSkeleton } from '../ds';

const API_URL = getApiUrl();

const countdown = (mins) => {
  if (mins <= 0) return 'purging now';
  const h = Math.floor(mins / 60);
  return h > 0 ? `${h}h ${mins % 60}m left` : `${mins}m left`;
};

export const DeletedLeadsPanel = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/leads/deleted`, { withCredentials: true });
      setRows(data.leads || []);
      setSelected([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load deleted leads');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const restore = async (ids) => {
    if (!ids.length) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/leads/restore`, { lead_ids: ids },
        { withCredentials: true });
      toast.success(`Restored ${data.restored} lead${data.restored === 1 ? '' : 's'}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Restore failed');
    } finally {
      setBusy(false);
    }
  };

  const toggle = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const allOn = rows.length > 0 && selected.length === rows.length;

  return (
    <div className="card p-4 sm:p-5" data-testid="deleted-leads-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-bold text-foreground">Recently deleted</h2>
          <p className="text-sm text-slate-500">
            Deleted leads stay here for 24 hours, then they are purged for good. Payments and bookings are never deleted.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {selected.length > 0 && (
            <button onClick={() => restore(selected)} disabled={busy}
              className="btn-primary text-sm px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="restore-selected-btn">
              <RotateCcw className="w-4 h-4" /> Restore {selected.length}
            </button>
          )}
          <button onClick={load} className="btn-secondary text-sm px-3 py-1.5 inline-flex items-center gap-1.5"
            data-testid="deleted-refresh-btn">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2.5 w-10">
                <input type="checkbox" className="rounded" checked={allOn} data-testid="deleted-select-all"
                  onChange={() => setSelected(allOn ? [] : rows.map((r) => r.lead_id))} />
              </th>
              <th className="px-3 py-2.5">Lead</th>
              <th className="px-3 py-2.5">Owner</th>
              <th className="px-3 py-2.5">Deleted</th>
              <th className="px-3 py-2.5">Restore window</th>
              <th className="px-3 py-2.5 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? <TableRowsSkeleton rows={4} cols={6} /> : rows.map((r) => (
              <tr key={r.lead_id} className="hover:bg-slate-50/70" data-testid={`deleted-row-${r.lead_id}`}>
                <td className="px-3 py-3">
                  <input type="checkbox" className="rounded" checked={selected.includes(r.lead_id)}
                    onChange={() => toggle(r.lead_id)} data-testid={`deleted-select-${r.lead_id}`} />
                </td>
                <td className="px-3 py-3">
                  <div className="font-semibold text-slate-800">{r.full_name || '—'}</div>
                  <div className="text-xs text-slate-500">{r.phone || r.email || ''}</div>
                  <div className="text-[11px] text-slate-400">{r.country || 'Unknown'} · {r.source || 'unknown source'}</div>
                </td>
                <td className="px-3 py-3 text-slate-600">
                  {r.assigned_sales_rep_name || r.assigned_pre_sales_name || '—'}
                </td>
                <td className="px-3 py-3 text-slate-600">
                  <div>{r.deleted_by_name || 'Unknown'}</div>
                  <div className="text-[11px] text-slate-400">{String(r.deleted_at || '').slice(0, 16).replace('T', ' ')}</div>
                </td>
                <td className="px-3 py-3">
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${r.minutes_left < 120 ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-600'}`}
                    data-testid={`deleted-countdown-${r.lead_id}`}>
                    {countdown(r.minutes_left)}
                  </span>
                </td>
                <td className="px-3 py-3 text-right">
                  <button onClick={() => restore([r.lead_id])} disabled={busy}
                    className="btn-secondary text-xs px-2.5 py-1 inline-flex items-center gap-1 disabled:opacity-50"
                    data-testid={`restore-lead-${r.lead_id}`}>
                    <RotateCcw className="w-3.5 h-3.5" /> Restore
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && rows.length === 0 && (
          <EmptyState icon={Trash2} title="Nothing deleted in the last 24 hours"
            description="Anything a super admin deletes shows up here with a countdown, so a mis-click is never final." />
        )}
      </div>
    </div>
  );
};

export default DeletedLeadsPanel;
